import {
  AfterContentInit,
  AfterViewChecked,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  forwardRef
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectItem } from 'primeng/api/selectitem';
import { PrimeTemplate } from 'primeng/api';
import { DomHandler } from 'primeng/dom';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { LmsObjUtilsService } from '../lms-obj-utils.service';

export const DROPDOWN_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => LmsDropdownComponent),
  multi: true
};

@Component({
  selector: 'lms-lms-dropdown',
  templateUrl: './lms-dropdown.component.html',
  styleUrls: ['./lms-dropdown.component.scss'],
  animations: [
    trigger('panelState', [
      state('hidden', style({
        opacity: 0
      })),
      state('visible', style({
        opacity: 1
      })),
      transition('visible => hidden', animate('400ms ease-in')),
      transition('hidden => visible', animate('400ms ease-out'))
    ])
  ],
  providers: [LmsObjUtilsService, DROPDOWN_VALUE_ACCESSOR]
})
export class LmsDropdownComponent implements OnInit, AfterViewInit, AfterContentInit, AfterViewChecked, OnDestroy, ControlValueAccessor {

  @Input() scrollHeight = '200px';

  @Input() filter: boolean;

  @Input() style: any;

  @Input() panelStyle: any;

  @Input() styleClass: string;

  @Input() panelStyleClass: string;

  @Input() disabled: boolean;

  @Input() readonly: boolean;

  @Input() autoWidth = true;

  @Input() required: boolean;

  @Input() editable: boolean;

  @Input() appendTo: any;

  @Input() tabindex: number;

  @Input() placeholder: string;

  @Input() filterPlaceholder: string;

  @Input() inputId: string;

  @Input() dataKey: string;

  @Input() filterBy = 'label';

  @Output() onChange: EventEmitter<any> = new EventEmitter();

  @Output() onFocus: EventEmitter<any> = new EventEmitter();

  @Output() onBlur: EventEmitter<any> = new EventEmitter();

  @ViewChild('container') containerViewChild: ElementRef;

  @ViewChild('panel') panelViewChild: ElementRef;

  @ViewChild('itemswrapper') itemsWrapperViewChild: ElementRef;

  @ViewChild('filter') filterViewChild: ElementRef;

  @ViewChild('in') focusViewChild: ElementRef;

  @ViewChild('editableInput') editableInputViewChild: ElementRef;

  @ContentChildren(PrimeTemplate) templates: QueryList<any>;

  public itemTemplate: TemplateRef<any>;

  optionsToDisplay: SelectItem[];

  hover: boolean;

  focus: boolean;

  selectedOption: SelectItem;

  highlightedOption: SelectItem;

  _options: SelectItem[];

  value: any;

  panelVisible = false;

  shown: boolean;

  documentClickListener: any;

  optionsChanged: boolean;

  panel: HTMLDivElement;

  container: HTMLDivElement;

  itemsWrapper: HTMLDivElement;

  initialized: boolean;

  selfClick: boolean;

  itemClick: boolean;

  hoveredItem: any;

  openByClick = false;

  selectedOptionUpdated: boolean;

  filterValue: string;
  onModelChange: Function = () => { };

  onModelTouched: Function = () => { };



  constructor(public el: ElementRef, public renderer: Renderer2, private cd: ChangeDetectorRef, public objectUtils: LmsObjUtilsService) { }

  ngAfterContentInit() {
    this.templates.forEach((item) => {
      switch (item.getType()) {
        case 'item':
          this.itemTemplate = item.template;
          break;

        default:
          this.itemTemplate = item.template;
          break;
      }
    });
  }

  ngOnInit() {
    this.optionsToDisplay = this.options;
    this.updateSelectedOption(null);
  }

  @Input() get options(): SelectItem[] {
    return this._options;
  }

  set options(opts: SelectItem[]) {
    this._options = opts;
    this.optionsToDisplay = this._options;
    this.updateSelectedOption(this.value);
    this.optionsChanged = true;

    if (this.filterValue && this.filterValue.length) {
      this.activateFilter();
    }
  }

  ngAfterViewInit() {
    this.container = <HTMLDivElement>this.containerViewChild.nativeElement;
    this.panel = <HTMLDivElement>this.panelViewChild.nativeElement;
    this.itemsWrapper = <HTMLDivElement>this.itemsWrapperViewChild.nativeElement;

    this.updateDimensions();
    this.initialized = true;

    if (this.appendTo) {
      if (this.appendTo === 'body') {
        document.body.appendChild(this.panel);
      } else {
        DomHandler.appendChild(this.panel, this.appendTo);
      }
    }
  }

  get label(): string {
    return (this.selectedOption ? this.selectedOption.label : this.placeholder);
  }

  updateEditableLabel(): void {
    if (this.editableInputViewChild && this.editableInputViewChild.nativeElement) {
      this.editableInputViewChild.nativeElement.value = this.value || (this.selectedOption ? this.selectedOption.label : '');
    }
  }

  onItemClick(event, option) {
    this.itemClick = true;
    this.selectItem(event, option);
    // this.focusViewChild.nativeElement.focus();

    this.hide();
  }

  selectItem(event, option) {
    if (this.selectedOption !== option) {
      this.selectedOption = option;
      this.value = option.value;

      this.onModelChange(this.value);
      this.updateEditableLabel();
      this.onChange.emit({
        originalEvent: event,
        value: this.value
      });
    }
    if (!this.panelVisible && this.selectedOption === option) {
      this.value = option.value;

      this.onModelChange(this.value);
      this.updateEditableLabel();
      this.onChange.emit({
        originalEvent: event,
        value: this.value
      });
    }

  }

  ngAfterViewChecked() {
    if (this.shown) {
      this.onShow();
      this.shown = false;
    }

    if (this.optionsChanged && this.panelVisible) {
      this.optionsChanged = false;
      this.alignPanel();
    }

    if (this.selectedOptionUpdated && this.itemsWrapper) {
      const selectedItem = DomHandler.findSingle(this.panel, 'li.p-highlight');
      if (selectedItem) {
        DomHandler.scrollInView(this.itemsWrapper, DomHandler.findSingle(this.panel, 'li.p-highlight'));
      }
      this.selectedOptionUpdated = false;
    }
  }

  writeValue(value: any): void {
    if (this.filter) {
      this.resetFilter();
    }

    this.value = value;
    this.updateSelectedOption(value);
    this.updateEditableLabel();
    this.cd.markForCheck();
  }

  resetFilter(): void {
    if (this.filterViewChild && this.filterViewChild.nativeElement) {
      this.filterViewChild.nativeElement.value = '';
    }

    this.optionsToDisplay = this.options;
  }

  updateSelectedOption(val: any): void {
    this.selectedOption = this.findOption(val, this.optionsToDisplay);
    if (!this.placeholder && !this.selectedOption && this.optionsToDisplay && this.optionsToDisplay.length && !this.editable) {
      this.selectedOption = this.optionsToDisplay[0];
    }
    this.selectedOptionUpdated = true;
  }

  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }

  setDisabledState(val: boolean): void {
    this.disabled = val;
  }

  updateDimensions() {
    if (this.autoWidth) {
      const select = DomHandler.findSingle(this.el.nativeElement, 'select');
      if (!this.style || (!this.style['width'] && !this.style['min-width'])) {
        this.el.nativeElement.children[0].style.width = select.offsetWidth + 30 + 'px';
      }
    }
  }

  onMouseclick(event) {
    if (this.disabled || this.readonly) {
      return;
    }

    this.selfClick = true;

    if (!this.itemClick) {
      this.openByClick = true;
      this.focusViewChild.nativeElement.focus();

      if (this.panelVisible) {
        this.hide();
      } else {
        this.show();

        if (this.filterViewChild !== undefined) {
          setTimeout(() => {
            this.filterViewChild.nativeElement.focus();
          }, 50);
        }
      }
    }
    if (this.optionsToDisplay && this.optionsToDisplay.length > 0) {
      this.highlightedOption = this.optionsToDisplay[0];
    }
  }

  onEditableInputClick(event) {
    this.itemClick = true;
    this.bindDocumentClickListener();
  }

  onEditableInputFocus(event) {
    this.focus = true;
    this.hide();
  }

  onEditableInputChange(event) {
    this.value = event.target.value;
    this.updateSelectedOption(this.value);
    this.onModelChange(this.value);
    this.onChange.emit({
      originalEvent: event,
      value: this.value
    });
  }

  onShow() {
    if (this.options && this.options.length) {
      this.alignPanel();
      this.bindDocumentClickListener();

      const selectedListItem = DomHandler.findSingle(this.itemsWrapper, '.p-dropdown-item.p-highlight');
      if (selectedListItem) {
        DomHandler.scrollInView(this.itemsWrapper, selectedListItem);
      }
    }
  }

  show() {
    if (this.appendTo) {
      this.panel.style.minWidth = DomHandler.getWidth(this.container) + 'px';
    }
    if (!this.filter) {
      setTimeout(() => {
        const panel = document.getElementById(this.inputId);
        if (panel) {
          const keydownEvent = this.filterBoxKeydown.bind(this);
          panel.removeEventListener('keydown', keydownEvent);
          panel.addEventListener('keydown', keydownEvent);
        }
      }, 10);
    }

    this.panel.style.zIndex = String(978); // this z-index applied to show this below leftoverlay panel
    this.panelVisible = true;
    this.shown = true;
    setTimeout(() => {
      if (this.filterViewChild !== undefined) {
        this.filterViewChild.nativeElement.focus();
      }
    }, 20);
  }

  hide() {
    this.panelVisible = false;
    this.openByClick = false;
  }

  alignPanel() {
    if (this.appendTo) {
      DomHandler.absolutePosition(this.panel, this.container);
    } else {
      DomHandler.relativePosition(this.panel, this.container);
    }
  }

  onInputFocus(event) {
    this.focus = true;
    this.onFocus.emit(event);
  }

  openPanelOnFocus() {

    if (!this.openByClick) {
      if (this.disabled || this.readonly) {
        return;
      }

      this.selfClick = false;

      if (!this.itemClick) {
        if (!this.panelVisible) {
          this.show();
        }
      }
      if (!this.highlightedOption && this.optionsToDisplay && this.optionsToDisplay.length > 0) {
        this.highlightedOption = this.optionsToDisplay[0];
      }
    }
  }

  onInputBlur(event) {
    this.focus = false;
    this.onModelTouched();
    this.onBlur.emit(event);
  }

  onKeydown(event) {
    if (this.readonly) {
      return;
    }

    const selectedItemIndex = this.selectedOption ? this.findOptionIndex(this.selectedOption.value, this.optionsToDisplay) : -1;

    switch (event.which) {
      // down
      case 40:
        if (!this.panelVisible && event.altKey) {
          this.show();
        } else {
          if (selectedItemIndex !== -1) {
            const nextItemIndex = selectedItemIndex + 1;
            if (nextItemIndex !== (this.optionsToDisplay.length)) {
              this.selectedOption = this.optionsToDisplay[nextItemIndex];
              this.selectedOptionUpdated = true;
              this.selectItem(event, this.selectedOption);
            }
          } else if (this.optionsToDisplay) {
            this.selectedOption = this.optionsToDisplay[0];
          }
        }

        event.preventDefault();

        break;

      // up
      case 38:
        if (selectedItemIndex > 0) {
          const prevItemIndex = selectedItemIndex - 1;
          this.selectedOption = this.optionsToDisplay[prevItemIndex];
          this.selectedOptionUpdated = true;
          this.selectItem(event, this.selectedOption);
        }

        event.preventDefault();
        break;

      // space
      case 32:
        this.panelVisible = !this.panelVisible;

        event.preventDefault();
        break;

      // enter
      case 13:
        this.hide();

        event.preventDefault();
        break;

      // escape and tab
      case 27:
      case 9:
        this.panelVisible = false;
        break;
    }
  }

  findOptionIndex(val: any, opts: SelectItem[]): number {
    let index: number = -1;
    if (opts) {
      for (let i = 0; i < opts.length; i++) {
        if ((val == null && opts[i].value == null) || this.objectUtils.equals(val, opts[i].value, this.dataKey)) {
          index = i;
          break;
        }
      }
    }

    return index;
  }

  findOption(val: any, opts: SelectItem[]): SelectItem {
    const index: number = this.findOptionIndex(val, opts);
    return (index !== -1) ? opts[index] : null;
  }

  onFilter(event): void {
    const inputValue = event.target.value.toLowerCase();
    if (inputValue && inputValue.length) {
      this.filterValue = inputValue;
      this.activateFilter();
    } else {
      this.filterValue = null;
      this.optionsToDisplay = this.options;
    }

    this.optionsChanged = true;
  }

  activateFilter() {
    const searchFields: string[] = this.filterBy.split(',');
    if (this.options && this.options.length) {
      this.optionsToDisplay = this.objectUtils.filter(this.options, searchFields, this.filterValue);
      this.optionsChanged = true;
    }
  }

  applyFocus(): void {
    if (this.editable) {
      DomHandler.findSingle(this.el.nativeElement, '.p-dropdown-label.p-inputtext').focus();
    } else {
      DomHandler.findSingle(this.el.nativeElement, 'input[readonly]').focus();
    }
  }

  bindDocumentClickListener() {
    if (!this.documentClickListener) {
      this.documentClickListener = this.renderer.listen('document', 'click', () => {
        if (!this.selfClick && !this.itemClick) {
          this.panelVisible = false;
          this.unbindDocumentClickListener();
        }

        this.selfClick = false;
        this.itemClick = false;
        this.cd.markForCheck();
      });
    }
  }

  unbindDocumentClickListener() {
    if (this.documentClickListener) {
      this.documentClickListener();
      this.documentClickListener = null;
    }
  }

  filterBoxKeydown(event) {
    if (event) {
      if (this.panelVisible) {
        const highlightItemIndex = this.highlightedOption ? this.findOptionIndex(this.highlightedOption.value, this.optionsToDisplay) : -1;
        switch (event.which) {
          // down arrow
          case 40:
            if (highlightItemIndex + 1 < this.optionsToDisplay.length) {
              this.highlightedOption = this.optionsToDisplay[highlightItemIndex + 1];
              this.selectedOptionUpdated = true;
            }
            event.preventDefault();
            break;
          // up arrow
          case 38:
            if (highlightItemIndex - 1 >= 0) {
              this.highlightedOption = this.optionsToDisplay[highlightItemIndex - 1];
              this.selectedOptionUpdated = true;
            }
            event.preventDefault();
            break;

          // escape
          case 27:
            this.hide();
            event.preventDefault();
            break;


          // tab
          case 9:
            if (this.highlightedOption) {
              this.selectItem(event, this.highlightedOption);
              this.hide();
            } else {
              if (this.optionsToDisplay && this.optionsToDisplay.length > 0) {
                this.highlightedOption = this.optionsToDisplay[0];
                this.selectItem(event, this.highlightedOption);
                this.hide();
              }
            }
            // event.preventDefault();
            break;
          // backspace
          case 8:
            if (this.optionsToDisplay && this.optionsToDisplay.length > 0) {
              this.highlightedOption = this.optionsToDisplay[0];
            }
            break;
          default:
            setTimeout(() => {
              if (this.optionsToDisplay && this.optionsToDisplay.length > 0) {
                this.highlightedOption = this.optionsToDisplay[0];
              }
            }, 15);
            break;
        }
      }
    }

  }

  ngOnDestroy() {
    this.initialized = false;

    this.unbindDocumentClickListener();

    if (this.appendTo) {
      this.el.nativeElement.appendChild(this.panel);
    }
  }

}
